import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { AnyPartItem } from "../../stores/slices/cartSlice";
import { ComboBox } from "../../ui/ComboBox/ComboBox";
import { Input } from "../../ui/Input/Input";
import { KeyValue } from "../../ui/KeyValue/KeyValue";
import { Placeholder } from "../../ui/Placeholder";
import { Spinner } from "../../ui/Spinner";
import { ChevronRightIcon } from "../../ui/icons/ChevronRightIcon";
import { PricingOption } from "../PriceSelect/getFlatPricingOptions";
import { PriceSelectEntry } from "../PriceSelect/usePriceSelectState";
import { PricingHookEntries } from "../PriceSelect/usePricing";

export const QuantitySelect: FunctionComponent<{
  busy?: boolean;
  className?: string;
  disabled?: boolean;
  entries: PricingHookEntries[];
  entryIndex: number;
  keyClassName?: string;
  onChange?: (pricingOption: PriceSelectEntry[]) => void;
  part: AnyPartItem | null;
  pricingOptions: PricingOption[];
  quantityInputClassName?: string;
  selectedPriceOption: PricingOption;
  setPricingIndex: (pricingIndex: number) => void;
  valueClassName?: string;
}> = ({
  busy,
  className,
  disabled,
  entries,
  entryIndex,
  keyClassName,
  onChange,
  part,
  pricingOptions,
  quantityInputClassName,
  selectedPriceOption,
  setPricingIndex,
  valueClassName,
}) => {
  const { t } = useTranslation();

  return (
    <KeyValue
      className={className}
      keyClassName={keyClassName}
      valueClassName={valueClassName}
    >
      <>{t("priceSelect.quantity")}:</>
      <Placeholder className="w-[2em] text-3xl" loading={!part}>
        {onChange ? (
          <ComboBox
            formatOption={(): string => {
              return selectedPriceOption.quantity || "";
            }}
            onSelectOption={(option): void => {
              if (!option) {
                return;
              }
              setPricingIndex(option.value);
              const currentEntry = entries[entryIndex];
              const currentPricingOption = currentEntry[0]?.pricingOptions.find(
                ({ value }) => value === option.value
              );
              if (onChange && currentPricingOption) {
                const selectedOptions = entries.map(
                  ([combinedPart, pricingOption]) => ({
                    combinedPart,
                    pricingOption,
                  })
                );
                selectedOptions[entryIndex].pricingOption =
                  currentPricingOption;
                onChange(selectedOptions);
              }
            }}
            aria-labelledby="price-options-label"
            id={`price-options-${part?.partDetails.primaryPart.itemId}`}
            options={pricingOptions}
            preferredPlacement="bottom"
          >
            <Input
              after={
                busy ? <Spinner /> : <ChevronRightIcon className="rotate-90" />
              }
              className={clsx(
                "h-8 w-[4rem] ml-1 pr-1 gap-1 -my-2",
                quantityInputClassName
              )}
              aria-label="selectedQuantity"
              disabled={disabled || busy}
              inputMode="numeric"
              readOnly
              value={selectedPriceOption.quantity}
            />
          </ComboBox>
        ) : (
          selectedPriceOption.quantity
        )}
      </Placeholder>
    </KeyValue>
  );
};
